import type { UserSummary } from 'types';
import type { ReportRead, ReportReadVariant } from 'definitions/report-read';
import { userFullName } from 'helpers/user-identity';
import {
  NEXT_STEP_CHOICES,
  OUTSIDE_FACTOR_CHOICES,
  labelOf,
  type ReportDraft,
  type ReportExpectation,
} from './report-questions';
import { challengerCount, comparedClause, isNearMiss } from './report-card';

/**
 * Everything the report card says, as a plain function of the answers, the experiment and the
 * server's read, so the copy is tested without rendering anything.
 *
 * The left of the card is facts from the platform, written from Statistics and never typed by
 * the user, so the headline is always true. The right is what the user said. The one thing that
 * joins them is the verdict chip, which is the user's own answer to "did it hold".
 */

export type Tone = 'ok' | 'warn' | 'danger' | 'dim';

export type StatCell = { label: string; value: string; tone: Tone | null };

export type ReportSummary = {
  chip: { label: string; tone: Tone };
  /** Device and experiment id. The page type joins them once the learnings library provides it. */
  tags: string[];
  headline: string;
  subline: string | null;
  /** Null when there are no statistics to show. */
  stats: StatCell[] | null;
  /** The brief's hypothesis; empty hides the block. */
  hypothesis: string;
  outcomeLine: string;
  lesson: string;
  /** Who last saved the report, for the badge beside the byline. Null when nobody is known. */
  author: UserSummary | null;
  /** "Name · date" of the last save; the badge stands beside it, so the name stays written. */
  byline: string | null;
  why: string;
  caveats: string[];
  caveatsNote: string;
  next: string | null;
};

const CHIPS: Record<ReportExpectation, { label: string; tone: Tone; outcome: string }> = {
  'as-expected': { label: 'held up', tone: 'ok', outcome: 'You marked this as held up.' },
  partly: { label: 'partly held', tone: 'warn', outcome: 'You marked this as partly held.' },
  opposite: { label: 'went the other way', tone: 'danger', outcome: 'You marked this as going the other way.' },
  'no-clear-result': { label: 'inconclusive', tone: 'warn', outcome: 'You marked this as inconclusive.' },
};

export const verdictChip = (expectation: ReportExpectation | null): { label: string; tone: Tone } =>
  expectation ? CHIPS[expectation] : { label: 'no verdict', tone: 'dim' };

/** 'add_to_cart' becomes 'Add to cart'. */
export const metricLabel = (name: string): string => {
  const spaced = name.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The variant the headline is about: the winner or loser when there is one, otherwise the one
 * furthest ahead, so a flat result still names what was tried.
 */
export const headlineVariant = (read: ReportRead): ReportReadVariant | null => {
  const named = read.variants.find((variant) => !variant.isControl && variant.name === read.verdict.variantName);
  if (named) return named;

  const challengers = read.variants.filter((variant) => !variant.isControl && variant.improvement !== null);
  return challengers.sort((a, b) => (b.improvement ?? 0) - (a.improvement ?? 0))[0] ?? null;
};

/** Under this size, in percent, a result that is not significant is too small to call a movement at all. */
const FLAT_BELOW_PCT = 1;

const signed = (value: number) => `${value < 0 ? '−' : '+'}${Math.abs(value).toFixed(1)}%`;

/** 'on mobile', 'on desktop and tablet', or 'across all devices'. */
const onDevices = (devices: string) =>
  devices === 'all devices' ? 'across all devices' : `on ${devices.replace(' · ', ' and ')}`;

const formatDate = (ms: number) => new Date(ms).toLocaleDateString();

const formatSavedDate = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** Confidence as the complement of the p-value, with the very small ones clamped rather than rounded to 100. */
const formatConfidence = (pValue: number | null): string | null => {
  if (pValue === null) return null;
  return pValue < 0.01 ? '>99%' : `${Math.round((1 - pValue) * 100)}%`;
};

type SummaryExperiment = {
  id: number;
  name: string;
  hypothesis: string;
  startsAt: number | null;
  endsAt: number | null;
  /** The primary metric's display name, e.g. 'Purchase' or 'Conversions'. */
  metric: string;
  devices: string;
};

type SummaryParams = {
  draft: ReportDraft;
  experiment: SummaryExperiment;
  read: ReportRead | null;
  author: UserSummary | null;
  /** When the report was last saved; null before it has been. */
  savedAt: number | null;
};

type FactsParams = Pick<SummaryParams, 'experiment' | 'read'>;
type Facts = Pick<ReportSummary, 'headline' | 'subline' | 'stats'>;

const buildFacts = ({ experiment, read }: FactsParams): Facts => {
  // No read, or no statistics behind it: say what is known and show no numbers rather than invent any.
  if (!read || read.variants.length === 0) {
    return {
      headline: experiment.name,
      subline: read ? 'No statistics are stored for this test yet.' : null,
      stats: null,
    };
  }

  const variant = headlineVariant(read);
  const improvement = variant?.improvement ?? null;
  const moved = !!variant && variant.significant && improvement !== null;
  const where = onDevices(experiment.devices);

  // "Did not move" claims there was no effect, which only a flat estimate supports. A +7.6% uplift whose
  // interval runs from -1% to +17% is a probable improvement that is not proven, and calling it "did not move"
  // overstates what the data says. So anything that is not significant but visibly moved says what was seen and
  // that it was not enough. When the raw p cleared 0.05 and the stricter corrected bar did not, it says why too.
  const flat = improvement === null || Math.abs(improvement) < FLAT_BELOW_PCT;
  const notEnough = !moved && !flat;
  const nearMiss = notEnough && isNearMiss(read.verdict);
  const direction = (improvement ?? 0) >= 0 ? 'rose' : 'fell';
  const size = Math.abs(improvement ?? 0).toFixed(1);

  const headline =
    moved && variant
      ? `${experiment.metric} ${direction} ${size}% ${where} with ${variant.name}.`
      : notEnough && variant
        ? `${experiment.metric} ${direction} ${size}% ${where} with ${variant.name}, but not by enough to call it${nearMiss ? comparedClause(challengerCount(read.variants)) : ''}.`
        : `${experiment.metric} did not move ${where}. No variant separated from control.`;

  const range =
    experiment.startsAt !== null && experiment.endsAt !== null
      ? `${formatDate(experiment.startsAt)} to ${formatDate(experiment.endsAt)}`
      : null;
  const subline = [variant ? `${variant.name} against control` : null, range].filter(Boolean).join(' · ') || null;

  const upliftTone: Tone | null = moved && improvement !== null ? (improvement >= 0 ? 'ok' : 'danger') : null;
  const confidence = formatConfidence(read.verdict.pValue);

  const stats: StatCell[] = [
    { label: 'Uplift', value: improvement === null ? '-' : signed(improvement), tone: upliftTone },
    { label: 'Confidence', value: confidence ?? '-', tone: null },
    { label: 'Visitors', value: read.visitors.toLocaleString(), tone: null },
    { label: 'Ran', value: read.days > 0 ? `${read.days}d` : '-', tone: null },
  ];

  return { headline, subline, stats };
};

export const buildReportSummary = ({ draft, experiment, read, author, savedAt }: SummaryParams): ReportSummary => {
  const chip = verdictChip(draft.expectation);

  return {
    chip,
    tags: [experiment.devices, String(experiment.id)],
    ...buildFacts({ experiment, read }),
    hypothesis: (read?.hypothesis || experiment.hypothesis).trim(),
    outcomeLine: draft.expectation ? CHIPS[draft.expectation].outcome : 'No verdict given yet.',
    lesson: draft.learning.trim(),
    // The badge stands beside the written name, so without a name there is no badge
    // either: a nameless icon next to a bare date would be a badge for nobody.
    author: savedAt !== null && author && userFullName(author) ? author : null,
    byline:
      savedAt === null
        ? null
        : [author ? userFullName(author) : '', formatSavedDate(savedAt)].filter(Boolean).join(' · '),
    why: draft.explanation.trim(),
    caveats: draft.outsideFactors.map((factor) => labelOf(OUTSIDE_FACTOR_CHOICES, factor)),
    // The note belongs to "Something else". Unticking it hides the textarea, so the card stops printing
    // text the author can no longer see; it is kept and comes back if the chip is ticked again.
    caveatsNote: draft.outsideFactors.includes('other') ? draft.outsideNote.trim() : '',
    next: draft.nextStep ? labelOf(NEXT_STEP_CHOICES, draft.nextStep) : null,
  };
};
